import { AdditionalProductBanner } from '@components/additionalProducts/AdditionalProductBanner';
import PoweredBy from '@components/pimster/poweredBy';
import NewTabLink from '@components/utils/links/newTabLink';
import Markdown from '@components/utils/mdText';
import ContentScroller from '@components/utils/scroller/ContentScroller';
import { TRANSITION_DURATION_IN_SECONDS } from '@constants';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import {
    OutboundLinkContext,
    OutboundLinkDestinationCategory,
    OutboundLinkShape,
} from '@enums';
import { useCompanyConfig } from '@lib/config/hooks';
import { useEmbedContext } from '@lib/embed/providers/EmbedProvider';
import { useVideoPlayerContext } from '@lib/pimster-stories/contexts/VideoPlayerContext';
import { SanitizedStoryPage } from '@lib/pimster-stories/types';
import {
    getAdditionalProducts,
    getEventsAndLinks,
} from '@lib/pimster-stories/utils/ctas';
import { VStack } from '@lib/uikit';
import { motion } from 'framer-motion';
import { ComponentProps, FC, useMemo } from 'react';

import { NavigationButtons } from '../buttons/NavigationButtons';
import { VideoPlayerButtons } from '../buttons/VideoPlayerButtons';
import { CtaHandler } from '../CtaHandler';

interface Props {
    storyPage: SanitizedStoryPage;
    withCtas: boolean;
    withNavigationButtons: boolean;
    withVideoPlayerButtons: boolean;
    customCtas?: React.ReactNode;
}

const Backdrop = styled.div`
    width: 100%;
    background: ${({ theme }) => theme.gradients.backdropDown};
    padding-block-start: ${({ theme }) => theme.spacing.lg};
`;

const Container = styled.div`
    padding-block: ${({ theme }) => theme.spacing.sm};
`;

const PaddingInlineContainer = styled.div`
    padding-inline: ${({ theme }) => theme.spacing.sm};
`;

const Box = styled.div`
    padding-inline: ${({ theme }) => theme.spacing.sm};
    display: flex;
    align-items: end;
    justify-content: space-between;
`;

const LinkComponent = ({ children, ...props }: ComponentProps<'a'>) => {
    return (
        <NewTabLink
            destination_category={OutboundLinkDestinationCategory.Unknown}
            context={OutboundLinkContext.StoryPage}
            shape={OutboundLinkShape.InlineText}
            href={props.href || ''}
            destination_name={props.href || ''}
            {...props}
        >
            {children}
        </NewTabLink>
    );
};

export const StoryFooter: FC<Props> = ({
    storyPage,
    withCtas,
    withNavigationButtons,
    withVideoPlayerButtons,
    customCtas,
}) => {
    const theme = useTheme();
    const { whiteLabel } = useCompanyConfig();
    const { isStoryBundle } = useEmbedContext();
    const { pauseVideo, playVideo } = useVideoPlayerContext();

    const eventsAndLinks = useMemo(() => {
        if (!withCtas) return;
        return getEventsAndLinks(storyPage.ctas);
    }, [withCtas, storyPage.ctas]);

    const additionalProducts = useMemo(() => {
        if (!withCtas) return;
        return getAdditionalProducts(storyPage.ctas);
    }, [withCtas, storyPage.ctas]);

    return (
        <Backdrop>
            <Container>
                <VStack spacing='xxs'>
                    {withVideoPlayerButtons && <VideoPlayerButtons />}
                    {storyPage.text && (
                        <Box>
                            <Markdown
                                markdown={storyPage.text}
                                textColor={theme.palette.common.white}
                                LinkComponent={LinkComponent}
                            />
                        </Box>
                    )}
                    {additionalProducts?.length ? (
                        <>
                            {additionalProducts.length === 1 ? (
                                <PaddingInlineContainer>
                                    <AdditionalProductBanner
                                        variant='outline'
                                        additionalProduct={
                                            additionalProducts[0]
                                                .Additional_product!
                                        }
                                        defaultSelectedVariant={
                                            additionalProducts[0]
                                                .Selected_variant
                                        }
                                        context={OutboundLinkContext.StoryPage}
                                        onDetailClose={playVideo}
                                        onDetailOpen={pauseVideo}
                                    />
                                </PaddingInlineContainer>
                            ) : (
                                <ContentScroller
                                    navigationPlacement='body'
                                    paddingInline='sm'
                                    listItemWidth='90%'
                                    scrollOptions={{
                                        behavior: 'smooth',
                                        inline: 'center',
                                    }}
                                >
                                    {additionalProducts.map((ap) => (
                                        <AdditionalProductBanner
                                            key={ap.Additional_product?.id}
                                            variant='outline'
                                            additionalProduct={
                                                ap.Additional_product!
                                            }
                                            defaultSelectedVariant={
                                                ap.Selected_variant
                                            }
                                            context={
                                                OutboundLinkContext.StoryPage
                                            }
                                            onDetailClose={playVideo}
                                            onDetailOpen={pauseVideo}
                                        />
                                    ))}
                                </ContentScroller>
                            )}
                        </>
                    ) : null}
                    {eventsAndLinks?.length ? (
                        <VStack spacing='xs' paddingInline='sm'>
                            {eventsAndLinks.map((cta, index) => (
                                <CtaHandler key={index} cta={cta} />
                            ))}
                        </VStack>
                    ) : null}
                    {customCtas && (
                        <VStack spacing='xs' paddingInline='sm'>
                            {customCtas}
                        </VStack>
                    )}
                    {withNavigationButtons && (
                        <PaddingInlineContainer>
                            <NavigationButtons />
                        </PaddingInlineContainer>
                    )}
                </VStack>
                {isStoryBundle && !whiteLabel.story && (
                    <motion.div
                        initial={{ height: 0, scale: 0.9, opacity: 0 }}
                        animate={{ height: 'auto', scale: 1, opacity: 1 }}
                        transition={{
                            duration: TRANSITION_DURATION_IN_SECONDS,
                            ease: 'easeInOut',
                        }}
                    >
                        <PoweredBy
                            priority
                            variant='light'
                            size='small'
                            marginTop='xs'
                            opacity={0.5}
                        />
                    </motion.div>
                )}
            </Container>
        </Backdrop>
    );
};
